import { Alert, Button, Empty, Segmented, Select, Skeleton, Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CATEGORY_COLORS, DonutChart, HBars, TrendChart } from './teamCharts'
import { getUsageSummary, listTeamMembers, teamErrorMessage } from './teamApi'
import { CATEGORY_KEYS, providerFilterValue, sharePercent, totalCount } from './teamCreditsUsage'
import type { ProviderBucket, UsageCategory, UsageRange, UsageSummary } from './teamTypes'
import { TeamAvatar, useProviderLabel, useTeamFormat } from './teamUi'

type RankRow = UsageSummary['ranking'][number]

/**
 * VistoeraTASK-00529 - 使用统计. Aggregated by the backend from the billed usage
 * ledger (see `team_usage_service`): task type = canonical operation key,
 * provider = ledger provider, member = the creator recorded on the source
 * record. Usage that cannot be attributed to a member is shown as such. No
 * prompt text, asset or output content is part of this page.
 */
export default function TeamUsageTab({ workspaceId }: { workspaceId: string }) {
  const { t } = useTranslation('team')
  const { num } = useTeamFormat()
  const providerLabel = useProviderLabel()
  const [range, setRange] = useState<UsageRange>('month')
  const [userId, setUserId] = useState<string | undefined>()
  const [category, setCategory] = useState<UsageCategory | undefined>()
  const [provider, setProvider] = useState<string | undefined>()
  const [data, setData] = useState<UsageSummary | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [members, setMembers] = useState<Array<{ value: string; label: string }>>([])
  const [providers, setProviders] = useState<ProviderBucket[]>([])
  const reqSeq = useRef(0)

  useEffect(() => {
    let cancelled = false
    listTeamMembers(workspaceId, { page: 1, page_size: 100 })
      .then((res) => {
        if (!cancelled) setMembers(res.data.items.map((m) => ({ value: m.user_id, label: m.display_name })))
      })
      .catch(() => undefined) // the filter simply stays empty; the page itself still works
    return () => {
      cancelled = true
    }
  }, [workspaceId])

  const load = useCallback(async () => {
    const seq = ++reqSeq.current
    setLoading(true)
    setError(null)
    try {
      const res = await getUsageSummary(workspaceId, { range, user_id: userId, category, provider })
      if (seq !== reqSeq.current) return
      setData(res.data)
      // keep the provider filter options stable: only learn them from an unfiltered-by-provider response
      if (!provider) setProviders(res.data.by_provider)
    } catch (e) {
      if (seq !== reqSeq.current) return
      setError(teamErrorMessage(e, t('usage.loadFailed')))
    } finally {
      if (seq === reqSeq.current) setLoading(false)
    }
  }, [workspaceId, range, userId, category, provider, t])

  useEffect(() => {
    void load()
  }, [load])

  const filtered = Boolean(userId || category || provider)
  const reset = () => {
    setUserId(undefined)
    setCategory(undefined)
    setProvider(undefined)
  }

  const trendSeries = useMemo(() => {
    const trend = data?.trend ?? []
    return [
      { key: 'tasks', name: t('usage.trend.tasks'), values: trend.map((p) => p.tasks), kind: 'bar' as const, color: 'var(--tm-accent)' },
      { key: 'credits', name: t('usage.trend.credits'), values: trend.map((p) => Number(p.credits) || 0), kind: 'line' as const, color: '#f59e0b' },
    ]
  }, [data, t])

  const rankColumns: ColumnsType<RankRow> = [
    { title: '#', key: 'rank', width: 48, render: (_: unknown, __: RankRow, i: number) => <span className="tm-rank">{i + 1}</span> },
    {
      title: t('usage.rank.member'),
      key: 'member',
      render: (_: unknown, r) => (
        <div className="tm-member-cell">
          <TeamAvatar name={r.display_name ?? '?'} size={30} round />
          <div className="tm-member-text">
            <div className="tm-member-name">{r.display_name ?? t('usage.rank.unknownMember')}</div>
            <div className="tm-hint">{r.email}</div>
          </div>
          {r.status === 'disabled' && <span className="tm-tag tm-tag-off">{t('status.disabled')}</span>}
        </div>
      ),
    },
    { title: t('usage.rank.tasks'), key: 'tasks', width: 90, align: 'right', render: (_: unknown, r) => num(r.tasks) },
    { title: t('usage.rank.credits'), key: 'credits', width: 110, align: 'right', render: (_: unknown, r) => <strong>{num(r.credits)}</strong> },
    { title: t('usage.rank.images'), key: 'images', width: 80, align: 'right', render: (_: unknown, r) => num(r.images) },
    { title: t('usage.rank.videos'), key: 'videos', width: 80, align: 'right', render: (_: unknown, r) => num(r.videos) },
  ]

  const body = () => {
    if (loading && !data) return <Skeleton active paragraph={{ rows: 10 }} />
    if (error && !data) {
      return (
        <Alert
          type="error"
          showIcon
          title={error}
          action={
            <Button size="small" onClick={() => void load()}>
              {t('common.retry')}
            </Button>
          }
        />
      )
    }
    if (!data) return null
    const totals = data.totals
    const typeTotal = totalCount(data.by_category)
    const kpis: Array<{ key: string; label: string; value: string }> = [
      { key: 'credits', label: t('usage.kpi.credits'), value: num(totals.credits) },
      { key: 'tasks', label: t('usage.kpi.tasks'), value: num(totals.tasks) },
      { key: 'images', label: t('usage.kpi.images'), value: num(totals.images) },
      { key: 'videos', label: t('usage.kpi.videos'), value: num(totals.videos) },
      { key: 'text', label: t('usage.kpi.text'), value: num(totals.text) },
    ]
    return (
      <>
        {error && <Alert type="warning" showIcon title={error} style={{ marginBottom: 12 }} />}
        {!data.has_data && (
          <Alert
            type="info"
            showIcon
            title={filtered ? t('usage.emptyFiltered') : t('usage.empty')}
            style={{ marginBottom: 16 }}
            data-testid="team-usage-empty"
          />
        )}

        <div className="tm-row tm-row-metrics tm-row-kpi">
          {kpis.map((k) => (
            <div className="tm-card tm-metric" key={k.key}>
              <span className="tm-card-label">{k.label}</span>
              <strong className="tm-metric-value" data-testid={`team-usage-kpi-${k.key}`}>
                {k.value}
              </strong>
              <span className="tm-hint">{t(`range.hint.${data.period.key}`)}</span>
            </div>
          ))}
        </div>

        <div className="tm-row tm-row-usage-charts">
          <section className="tm-card tm-trend-card">
            <div className="tm-card-head">
              <span className="tm-card-title">{t('usage.trend.title')}</span>
            </div>
            <TrendChart
              labels={data.trend.map((p) => p.date)}
              series={data.has_data ? trendSeries : []}
              format={(k, v) => (k === 'credits' ? num(v) : String(v))}
              ariaLabel={t('usage.trend.title')}
              emptyText={t('usage.trend.empty')}
            />
            {data.has_data && (
              <div className="tm-chart-legend">
                {trendSeries.map((s) => (
                  <span key={s.key}>
                    <i className="tm-dot" style={{ background: s.color }} />
                    {s.name}
                  </span>
                ))}
              </div>
            )}
          </section>

          <section className="tm-card tm-dist-card">
            <div className="tm-card-head">
              <span className="tm-card-title">{t('usage.type.title')}</span>
            </div>
            {data.by_category.length ? (
              <div className="tm-dist-body">
                <DonutChart
                  ariaLabel={t('usage.type.title')}
                  items={data.by_category.map((c) => ({ key: c.key, value: c.count, color: CATEGORY_COLORS[c.key] }))}
                />
                <ul className="tm-legend">
                  {data.by_category.map((c) => (
                    <li key={c.key}>
                      <span className="tm-dot" style={{ background: CATEGORY_COLORS[c.key] }} />
                      <span>{t(`overview.dist.${c.key}`)}</span>
                      <strong>
                        {num(c.count)} · {sharePercent(c.count, typeTotal)}%
                      </strong>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('usage.noData')} />
            )}
          </section>
        </div>

        <div className="tm-row tm-row-usage-lists">
          <section className="tm-card" data-testid="team-usage-ranking">
            <div className="tm-card-head">
              <span className="tm-card-title">{t('usage.rank.title')}</span>
            </div>
            {data.ranking.length ? (
              <Table<RankRow>
                rowKey="user_id"
                size="small"
                columns={rankColumns}
                dataSource={data.ranking}
                pagination={data.ranking.length > 10 ? { pageSize: 10, hideOnSinglePage: true, showSizeChanger: false } : false}
                scroll={{ x: 520 }}
              />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('usage.noData')} />
            )}
            {data.unattributed && (
              <div className="tm-hint tm-unattributed" data-testid="team-usage-unattributed">
                {t('usage.rank.unattributed', { count: data.unattributed.tasks, credits: num(data.unattributed.credits) })}
              </div>
            )}
          </section>

          <section className="tm-card tm-top-card">
            <div className="tm-card-head">
              <span className="tm-card-title">{t('usage.provider.title')}</span>
            </div>
            {data.by_provider.length ? (
              <HBars
                rows={data.by_provider.slice(0, 10).map((p) => ({
                  key: p.provider_key ?? 'unknown',
                  label: providerLabel(p.provider_key),
                  value: p.count,
                  right: t('usage.provider.row', { count: p.count, credits: num(p.credits) }),
                }))}
              />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('usage.noData')} />
            )}
          </section>
        </div>
        <div className="tm-hint">{t('usage.footnote')}</div>
      </>
    )
  }

  return (
    <div className="tm-usage" data-testid="team-usage">
      <section className="tm-card tm-filter-card">
        <div className="tm-toolbar" style={{ marginBottom: 0 }}>
          <Segmented<UsageRange>
            value={range}
            onChange={setRange}
            options={[
              { value: 'month', label: t('range.month') },
              { value: '7d', label: t('range.d7') },
              { value: '30d', label: t('range.d30') },
            ]}
            data-testid="team-usage-range"
          />
          <Select<string>
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={t('usage.filter.member')}
            value={userId}
            onChange={setUserId}
            options={members}
            style={{ width: 180 }}
          />
          <Select<UsageCategory>
            allowClear
            placeholder={t('usage.filter.type')}
            value={category}
            onChange={setCategory}
            options={CATEGORY_KEYS.map((k) => ({ value: k, label: t(`overview.dist.${k}`) }))}
            style={{ width: 150 }}
          />
          <Select<string>
            allowClear
            placeholder={t('usage.filter.provider')}
            value={provider}
            onChange={setProvider}
            options={providers.map((p) => ({ value: providerFilterValue(p.provider_key), label: providerLabel(p.provider_key) }))}
            style={{ width: 170 }}
          />
          {filtered && (
            <Button type="link" onClick={reset}>
              {t('usage.filter.reset')}
            </Button>
          )}
        </div>
      </section>
      {body()}
    </div>
  )
}
